// Can make, worked out in the page as the director edits a product's steps
// (Build steps, 27 Oct 2026) -- the same rules as the server's
// (backend utils/stock.js canMake, utils/stockPacking.js): stock held at a
// stage is used first; a moulded part counts only what is on the shelf;
// metres and kilograms are taken as they come, pieces and boxes whole;
// packing from Packaging takes the place of packing typed into a list, and
// packing never counted yet does not stop it.

const EPS = 1e-9;
const whole = (x) => Math.ceil(x - EPS);

// A saved quantity back to the fraction it was typed as: 0.041667 is 1/24.
export function exact(q) {
  const x = Number(q);
  if (!(x > 0) || Number.isInteger(x)) return x;
  for (let d = 2; d <= 1000; d++) {
    const n = Math.round(x * d);
    if (n > 0 && Math.abs(n / d - x) <= 5e-7 + EPS) return n / d;
  }
  return x;
}

// "1/30", "0,5", "2" -> a number, or null.
export function parseQty(v) {
  const s = String(v ?? '').trim().replace(',', '.');
  const f = s.match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/);
  const n = f ? Number(f[1]) / Number(f[2]) : Number(s);
  return s !== '' && Number.isFinite(n) && n > 0 ? n : null;
}

// The items below root, each after everything that takes it; null on a loop.
function orderFrom(root, inputs) {
  const below = new Map(), seen = new Set([root]), walk = [root];
  while (walk.length) {
    const u = walk.pop();
    for (const e of inputs.get(u) || []) {
      below.set(e.input_id, (below.get(e.input_id) || 0) + 1);
      if (!seen.has(e.input_id)) { seen.add(e.input_id); walk.push(e.input_id); }
    }
  }
  const out = [], ready = [root], left = new Map(below);
  while (ready.length) {
    const u = ready.shift();
    out.push(u);
    for (const e of inputs.get(u) || []) {
      left.set(e.input_id, left.get(e.input_id) - 1);
      if (left.get(e.input_id) === 0) ready.push(e.input_id);
    }
  }
  return out.length === seen.size ? out : null;
}

// What making n of root takes, level by level: per item { need, have,
// fromShelf, make, short, leaf }. { loop: true } when the lists go round.
//   opts: { moulded: Set, continuous: Set }
export function explode(root, n, inputs, stock, { moulded = new Set(), continuous = new Set() } = {}) {
  const order = orderFrom(root, inputs);
  if (!order) return { loop: true, rows: new Map() };
  const have = (id) => Math.max(0, Number(stock.get(id)) || 0);
  const need = new Map([[root, n]]);
  const rows = new Map();
  for (const u of order) {
    const r = continuous.has(u) ? Math.round((need.get(u) || 0) * 1e6) / 1e6 : whole(need.get(u) || 0);
    if (r <= 0) continue;
    const h = u === root ? 0 : have(u);
    const fromShelf = Math.min(r, h);
    const list = u !== root && moulded.has(u) ? [] : inputs.get(u) || [];
    const leaf = u !== root && !list.length;
    const make = leaf ? 0 : r - fromShelf;
    rows.set(u, { id: u, need: r, have: h, fromShelf, make, short: leaf ? Math.max(0, r - h) : 0, leaf });
    if (!leaf) for (const e of list) need.set(e.input_id, (need.get(e.input_id) || 0) + make * exact(e.qty));
  }
  return { rows };
}

// How many more of root stock would make, and what stops one more.
export function canMake(root, inputs, stock, opts = {}) {
  if (!(inputs.get(root) || []).length) return { n: 0, short: [] };
  const shortOf = (n) => { const x = explode(root, n, inputs, stock, opts); return x.loop ? null : [...x.rows.values()].filter(r => r.short > 0); };
  const first = shortOf(1);
  if (first === null) return { n: 0, short: [], loop: true };
  if (first.length) return { n: 0, short: first };
  let lo = 1, hi = 2;
  while (!shortOf(hi).length && hi < 1e9) { lo = hi; hi *= 2; }
  while (hi - lo > 1) { const mid = Math.floor((lo + hi) / 2); if (shortOf(mid).length) hi = mid; else lo = mid; }
  return { n: lo, short: shortOf(lo + 1) };
}

// The packed product's list as Stock counts it: its own lines, with the
// packing from Packaging in place of packing typed by hand. `counting`:
// leave out packing never counted yet (for can make).
//   packing: { lines: [{ container_id, input_id, qty, left_out }] }
export function withPacking(list, packing, packagingOf, counting = true) {
  if (!packing) return list;
  // Packing a step below already takes (taken_below) is not taken again.
  const lines = packing.lines.filter(l => l.input_id != null && l.qty != null && l.qty > 0 && !l.taken_below);
  const covered = new Set(packing.lines.map(l => Number(l.container_id)));
  const own = list.filter(e => !lines.some(l => l.input_id === e.input_id) && !covered.has(Number(packagingOf(e.input_id))));
  return [...own, ...lines.filter(l => !counting || !l.left_out).map(l => ({ input_id: l.input_id, qty: l.qty, from: 'packaging' }))];
}
